import { useState, useEffect } from "react";
import { useVerifyOrganization, useDismissVerificationRequest, useAllSheltersForVerification, useDeleteShelter } from "@/hooks/useOrganizationVerification";
import { useShelterAdminNotes, useSaveShelterAdminNote, useToggleShelterFollowUp } from "@/hooks/useShelterAdminNotes";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, BadgeCheck, BadgeX, MapPin, Clock, Search, XCircle, Building, X, Trash2, Pencil, StickyNote, Check, Flag, Link2, Building2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { ShelterEditDialog } from "./ShelterEditDialog";
import { InlineGenerateClaimLinkButton } from "./InlineGenerateClaimLinkButton";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Shelter } from "@/types/shelter";

interface AdminNoteInlineProps {
  shelterId: string;
  initialNotes: string;
  initialUpdatedAt: string | null;
}

const AdminNoteInline = ({ shelterId, initialNotes, initialUpdatedAt }: AdminNoteInlineProps) => {
  const [value, setValue] = useState(initialNotes);
  const saveNote = useSaveShelterAdminNote();

  useEffect(() => {
    setValue(initialNotes);
  }, [initialNotes, shelterId]);

  const dirty = value !== initialNotes;

  const handleSave = async () => {
    try {
      await saveNote.mutateAsync({ shelterId, notes: value });
      toast.success("Notatka zapisana");
    } catch (error: any) {
      toast.error(error.message || "Nie udało się zapisać notatki");
    }
  };

  return (
    <div className="mt-3 p-3 rounded-md border border-border/60 bg-muted/30 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <StickyNote className="w-3.5 h-3.5" />
          Notatka administratora
        </div>
        {initialUpdatedAt && !dirty && (
          <span className="text-xs text-muted-foreground">
            Zapisano {format(new Date(initialUpdatedAt), "d MMM yyyy, HH:mm", { locale: pl })}
          </span>
        )}
      </div>
      <Textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Dodaj prywatną notatkę widoczną tylko dla administratorów..."
        className="min-h-[70px] text-sm bg-background"
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          variant={dirty ? "default" : "outline"}
          onClick={handleSave}
          disabled={!dirty || saveNote.isPending}
        >
          {saveNote.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
          ) : (
            <Check className="w-3.5 h-3.5 mr-1" />
          )}
          Zapisz notatkę
        </Button>
      </div>
    </div>
  );
};

export const OrganizationVerificationTab = () => {
  const { data: shelters, isLoading: sheltersLoading } = useAllSheltersForVerification(true);
  const verifyOrganization = useVerifyOrganization();
  const dismissRequest = useDismissVerificationRequest();
  const deleteShelter = useDeleteShelter();
  const toggleFollowUp = useToggleShelterFollowUp();

  const { data: adminNotesMap } = useShelterAdminNotes(true);

  // Fetch which shelters have an organization account (shelter_owners)
  const { data: orgShelterIds } = useQuery({
    queryKey: ["shelter-owners-ids"],
    queryFn: async () => {
      const { data, error } = await supabase.from("shelter_owners").select("shelter_id");
      if (error) throw error;
      return new Set((data || []).map((r: any) => r.shelter_id as string));
    },
  });

  // Fetch which shelters have ever received a magic link (claim token)
  const { data: tokenShelterIds } = useQuery({
    queryKey: ["shelter-claim-token-ids"],
    queryFn: async () => {
      const { data, error } = await supabase.from("shelter_claim_tokens").select("shelter_id");
      if (error) throw error;
      return new Set((data || []).map((r: any) => r.shelter_id as string));
    },
  });

  // Fetch which shelters have an ACTIVE magic link (not used + not expired)
  const { data: activeTokenShelterIds } = useQuery({
    queryKey: ["shelter-claim-token-active-ids"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shelter_claim_tokens")
        .select("shelter_id, expires_at, used_at")
        .is("used_at", null)
        .gt("expires_at", new Date().toISOString());
      if (error) throw error;
      return new Set((data || []).map((r: any) => r.shelter_id as string));
    },
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyWithNotes, setShowOnlyWithNotes] = useState(false);
  const [showOnlyFollowUp, setShowOnlyFollowUp] = useState(false);
  const [showOnlyOrg, setShowOnlyOrg] = useState(false);
  const [verifiedFilter, setVerifiedFilter] = useState<"all" | "verified" | "unverified">("all");
  const [linkFilter, setLinkFilter] = useState<"all" | "active" | "inactive" | "none">("all");
  const [dataFilter, setDataFilter] = useState<"all" | "complete" | "incomplete">("all");
  const [sortBy, setSortBy] = useState<"name" | "follow_up" | "notes" | "data_empty" | "verified">("name");
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [editingShelter, setEditingShelter] = useState<Shelter | null>(null);

  const toggleTri = <T extends string>(current: T, value: T, fallback: T): T =>
    current === value ? fallback : value;

  const toggleNoteExpand = (id: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleFollowUp = async (shelterId: string, current: boolean) => {
    try {
      await toggleFollowUp.mutateAsync({ shelterId, followUp: !current });
      toast.success(!current ? "Oznaczono: wymaga działania" : "Usunięto oznaczenie");
    } catch (error: any) {
      toast.error(error.message || "Nie udało się zaktualizować oznaczenia");
    }
  };

  // Shelters with pending verification requests
  const pendingRequests = shelters?.filter(s => 
    s.organization_verification_pending && !s.organization_verified
  ) || [];

  // All shelters for manual verification section
  const TOTAL_DATA_CHECKS = 7;
  const computeFilledScore = (s: Shelter) =>
    (s.phone || s.email || s.website ? 1 : 0) +
    (s.current_needs && s.current_needs.trim().length > 0 ? 1 : 0) +
    (s.description && s.description.trim().length > 0 ? 1 : 0) +
    (s.address && s.address.trim().length > 0 ? 1 : 0) +
    (s.volunteering_available !== null && s.volunteering_available !== undefined ? 1 : 0) +
    (s.dog_walking_available !== null && s.dog_walking_available !== undefined ? 1 : 0) +
    (s.volunteer_info && s.volunteer_info.trim().length > 0 ? 1 : 0);

  const filteredShelters = (shelters?.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.city.toLowerCase().includes(searchTerm.toLowerCase());
    const noteRow = adminNotesMap?.[s.id];
    const noteText = noteRow?.notes ?? "";
    const followUp = noteRow?.follow_up_needed ?? false;
    const matchesNotes = !showOnlyWithNotes || noteText.trim().length > 0;
    const matchesFollowUp = !showOnlyFollowUp || followUp;
    const matchesOrg = !showOnlyOrg || (orgShelterIds?.has(s.id) ?? false);
    const hasAnyTok = tokenShelterIds?.has(s.id) ?? false;
    const hasActiveTok = activeTokenShelterIds?.has(s.id) ?? false;
    const matchesLink =
      linkFilter === "all" ||
      (linkFilter === "active" && hasActiveTok) ||
      (linkFilter === "inactive" && hasAnyTok && !hasActiveTok) ||
      (linkFilter === "none" && !hasAnyTok);
    const matchesVerified =
      verifiedFilter === "all" ||
      (verifiedFilter === "verified" && s.organization_verified) ||
      (verifiedFilter === "unverified" && !s.organization_verified);
    const score = computeFilledScore(s);
    const matchesData =
      dataFilter === "all" ||
      (dataFilter === "complete" && score === TOTAL_DATA_CHECKS) ||
      (dataFilter === "incomplete" && score < TOTAL_DATA_CHECKS);
    return matchesSearch && matchesNotes && matchesFollowUp && matchesOrg && matchesLink && matchesVerified && matchesData;
  }) || []).slice().sort((a, b) => {
    const noteA = adminNotesMap?.[a.id];
    const noteB = adminNotesMap?.[b.id];
    if (sortBy === "follow_up") {
      const fa = noteA?.follow_up_needed ? 1 : 0;
      const fb = noteB?.follow_up_needed ? 1 : 0;
      if (fa !== fb) return fb - fa;
    } else if (sortBy === "notes") {
      const na = (noteA?.notes ?? "").trim().length > 0 ? 1 : 0;
      const nb = (noteB?.notes ?? "").trim().length > 0 ? 1 : 0;
      if (na !== nb) return nb - na;
      const ta = noteA?.updated_at ? new Date(noteA.updated_at).getTime() : 0;
      const tb = noteB?.updated_at ? new Date(noteB.updated_at).getTime() : 0;
      if (ta !== tb) return tb - ta;
    } else if (sortBy === "data_empty") {
      const da = computeFilledScore(a);
      const db = computeFilledScore(b);
      if (da !== db) return da - db;
    } else if (sortBy === "verified") {
      const va = a.organization_verified ? 1 : 0;
      const vb = b.organization_verified ? 1 : 0;
      if (va !== vb) return vb - va;
    }
    return a.name.localeCompare(b.name, "pl");
  });

  const handleVerify = async (shelterId: string, shelterName: string) => {
    try {
      await verifyOrganization.mutateAsync({ shelterId, verified: true });
      toast.success(`${shelterName} zostało zweryfikowane jako organizacja`);
    } catch (error: any) {
      toast.error(error.message || "Błąd weryfikacji");
    }
  };

  const handleUnverify = async (shelterId: string, shelterName: string) => {
    try {
      await verifyOrganization.mutateAsync({ shelterId, verified: false });
      toast.success(`Usunięto weryfikację dla ${shelterName}`);
    } catch (error: any) {
      toast.error(error.message || "Błąd usuwania weryfikacji");
    }
  };

  const handleDismissRequest = async (shelterId: string, shelterName: string) => {
    try {
      await dismissRequest.mutateAsync(shelterId);
      toast.success(`Odrzucono prośbę o weryfikację dla ${shelterName}`);
    } catch (error: any) {
      toast.error(error.message || "Błąd odrzucania prośby");
    }
  };

  const handleDeleteShelter = async (shelterId: string, shelterName: string) => {
    try {
      await deleteShelter.mutateAsync(shelterId);
      toast.success(`Schronisko "${shelterName}" zostało usunięte z bazy`);
    } catch (error: any) {
      toast.error(error.message || "Błąd usuwania schroniska");
    }
  };

  return (
    <div className="space-y-8">
      {/* Pending organization verification requests */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-lg">Oczekujące prośby o weryfikację</h2>
          <Badge variant="secondary">
            {pendingRequests.length} oczekujących
          </Badge>
        </div>

        {sheltersLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : pendingRequests.length > 0 ? (
          <div className="space-y-3">
            {pendingRequests.map((shelter) => (
              <div 
                key={shelter.id} 
                className="p-4 rounded-lg border border-primary/20 bg-primary/[0.03] hover:bg-primary/[0.06] transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-medium">{shelter.name}</p>
                      <Badge variant="outline" className="text-primary/70 border-primary/30">
                        <BadgeCheck className="w-3 h-3 mr-1" />
                        Prośba o weryfikację
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {shelter.city}{shelter.region && `, ${shelter.region}`}
                    </p>
                    {shelter.organization_verification_requested_at && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        Zgłoszono: {format(new Date(shelter.organization_verification_requested_at), "d MMM yyyy, HH:mm", { locale: pl })}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleVerify(shelter.id, shelter.name)}
                      disabled={verifyOrganization.isPending}
                      className="h-7 px-2 text-xs"
                    >
                      {verifyOrganization.isPending ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <BadgeCheck className="w-3.5 h-3.5" />
                      )}
                      <span className="ml-1">Zweryfikuj</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDismissRequest(shelter.id, shelter.name)}
                      disabled={dismissRequest.isPending}
                      className="h-7 px-2 text-xs"
                    >
                      {dismissRequest.isPending ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <X className="w-3.5 h-3.5" />
                      )}
                      <span className="hidden sm:inline ml-1">Odrzuć</span>
                    </Button>
                  </div>
                </div>
                
                <div className="mt-3 p-2 bg-background/50 rounded text-xs text-muted-foreground">
                  <strong>Wskazówka:</strong> Skontaktuj się z organizacją przez email lub Facebook, aby zweryfikować tożsamość przed zatwierdzeniem.
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <BadgeCheck className="w-10 h-10 mx-auto mb-3 text-primary" />
              <p className="font-medium">Brak oczekujących próśb</p>
              <p className="text-sm">Żadna organizacja nie czeka na weryfikację</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Manual verification section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-lg">Ręczna weryfikacja organizacji</h2>
          <Badge variant="outline">
            {shelters?.filter(s => s.organization_verified).length || 0} zweryfikowanych
          </Badge>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Szukaj schroniska po nazwie lub mieście..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-8"
            />
          </div>

          {/* Verification group */}
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => setVerifiedFilter(toggleTri(verifiedFilter, "verified", "all"))}
              className={`inline-flex items-center gap-1 h-7 px-2 text-xs border-r border-border transition-colors ${
                verifiedFilter === "verified"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted text-primary"
              }`}
              title="Tylko zweryfikowane"
            >
              <BadgeCheck className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Zweryfikowane</span>
            </button>
            <button
              type="button"
              onClick={() => setVerifiedFilter(toggleTri(verifiedFilter, "unverified", "all"))}
              className={`inline-flex items-center gap-1 h-7 px-2 text-xs transition-colors ${
                verifiedFilter === "unverified"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted text-muted-foreground"
              }`}
              title="Tylko niezweryfikowane"
            >
              <XCircle className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Niezweryfikowane</span>
            </button>
          </div>

          {/* Magic link group */}
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => setLinkFilter(toggleTri(linkFilter, "active", "all"))}
              className={`inline-flex items-center gap-1 h-7 px-2 text-xs border-r border-border transition-colors ${
                linkFilter === "active"
                  ? "bg-amber-500 text-white"
                  : "hover:bg-muted text-amber-700 dark:text-amber-400"
              }`}
              title="Magic link aktywny"
            >
              <KeyRound className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Link aktywny</span>
            </button>
            <button
              type="button"
              onClick={() => setLinkFilter(toggleTri(linkFilter, "inactive", "all"))}
              className={`inline-flex items-center gap-1 h-7 px-2 text-xs border-r border-border transition-colors ${
                linkFilter === "inactive"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted text-muted-foreground"
              }`}
              title="Link wygasł lub został użyty"
            >
              <KeyRound className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Link nieaktywny</span>
            </button>
            <button
              type="button"
              onClick={() => setLinkFilter(toggleTri(linkFilter, "none", "all"))}
              className={`inline-flex items-center gap-1 h-7 px-2 text-xs transition-colors ${
                linkFilter === "none"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted text-muted-foreground"
              }`}
              title="Brak magic linku"
            >
              <KeyRound className="w-3.5 h-3.5 opacity-50" />
              <span className="hidden sm:inline">Bez linku</span>
            </button>
          </div>

          <Button
            size="sm"
            variant={showOnlyOrg ? "default" : "outline"}
            onClick={() => setShowOnlyOrg(!showOnlyOrg)}
            className="h-7 px-2 text-xs"
          >
            <Building2 className="w-3.5 h-3.5 sm:mr-1" />
            <span className="hidden sm:inline">Organizacja</span>
          </Button>
          <Button
            size="sm"
            variant={showOnlyWithNotes ? "default" : "outline"}
            onClick={() => setShowOnlyWithNotes(!showOnlyWithNotes)}
            className="h-7 px-2 text-xs"
          >
            <StickyNote className="w-3.5 h-3.5 sm:mr-1" />
            <span className="hidden sm:inline">Z notatką</span>
          </Button>
          <Button
            size="sm"
            variant={showOnlyFollowUp ? "default" : "outline"}
            onClick={() => setShowOnlyFollowUp(!showOnlyFollowUp)}
            className={`h-7 px-2 text-xs ${showOnlyFollowUp ? "" : "text-amber-700 border-amber-500/40 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30"}`}
          >
            <Flag className="w-3.5 h-3.5 sm:mr-1" />
            <span className="hidden sm:inline">Wymaga działania</span>
          </Button>

          <Select value={dataFilter} onValueChange={(v) => setDataFilter(v as typeof dataFilter)}>
            <SelectTrigger className="h-7 text-xs w-auto sm:w-[150px]">
              <SelectValue placeholder="Status danych" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie profile</SelectItem>
              <SelectItem value="complete">Tylko kompletne</SelectItem>
              <SelectItem value="incomplete">Tylko z brakami</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
            <SelectTrigger className="h-7 text-xs w-auto sm:w-[200px]">
              <SelectValue placeholder="Sortuj" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Alfabetycznie (A-Z)</SelectItem>
              <SelectItem value="follow_up">Wymaga działania - na górze</SelectItem>
              <SelectItem value="notes">Z notatką - na górze</SelectItem>
              <SelectItem value="data_empty">Puste profile - na górze</SelectItem>
              <SelectItem value="verified">Zweryfikowane - na górze</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {sheltersLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : filteredShelters.length > 0 ? (
          <div className="space-y-2">
            {filteredShelters.map((shelter) => {
              const noteData = adminNotesMap?.[shelter.id];
              const noteText = noteData?.notes ?? "";
              const hasNote = noteText.trim().length > 0;
              const followUp = noteData?.follow_up_needed ?? false;
              const isExpanded = expandedNotes.has(shelter.id);
              const hasContact = !!(shelter.phone || shelter.email || shelter.website);
              const hasNeeds = !!(shelter.current_needs && shelter.current_needs.trim().length > 0);
              const hasDescription = !!(shelter.description && shelter.description.trim().length > 0);
              const hasAddress = !!(shelter.address && shelter.address.trim().length > 0);
              const hasVolunteering = shelter.volunteering_available !== null && shelter.volunteering_available !== undefined;
              const hasWalking = shelter.dog_walking_available !== null && shelter.dog_walking_available !== undefined;
              const hasVolunteerInfo = !!(shelter.volunteer_info && shelter.volunteer_info.trim().length > 0);
              const checks = [hasContact, hasNeeds, hasDescription, hasAddress, hasVolunteering, hasWalking, hasVolunteerInfo];
              const filledCount = checks.filter(Boolean).length;
              const dataStatus: "empty" | "partial" | "complete" =
                filledCount === 0 ? "empty" : filledCount === checks.length ? "complete" : "partial";
              const dataStatusColor =
                dataStatus === "empty"
                  ? "bg-destructive"
                  : dataStatus === "partial"
                  ? "bg-amber-500"
                  : "bg-emerald-500";
              const missingParts = [
                hasContact ? null : "brak kontaktu",
                hasNeeds ? null : "brak potrzeb",
                hasDescription ? null : "brak opisu",
                hasAddress ? null : "brak adresu",
                hasVolunteering ? null : "brak info o wolontariacie",
                hasWalking ? null : "brak info o wyprowadzaniu",
                hasVolunteerInfo ? null : "brak opisu wolontariatu",
              ].filter(Boolean);
              const dataStatusLabel =
                dataStatus === "empty"
                  ? "Pusty profil"
                  : dataStatus === "complete"
                  ? "Profil kompletny (wszystkie pola wypełnione)"
                  : `Profil niekompletny: ${missingParts.join(", ")}`;
              const hasAnyToken = tokenShelterIds?.has(shelter.id) ?? false;
              const hasActiveToken = activeTokenShelterIds?.has(shelter.id) ?? false;
              const hasOrgAccount = orgShelterIds?.has(shelter.id) ?? false;
              const verified = shelter.organization_verified;

              type StatusPill = {
                key: string;
                icon: typeof BadgeCheck;
                label: string;
                tooltip: string;
                tone: "primary" | "amber" | "muted" | "neutral";
              };
              const statusPills: StatusPill[] = [
                verified
                  ? { key: "ver", icon: BadgeCheck, label: "Zweryfikowane", tooltip: "Schronisko zostało ręcznie zweryfikowane przez administratora.", tone: "primary" }
                  : { key: "ver", icon: XCircle, label: "Niezweryfikowane", tooltip: "Schronisko nie zostało jeszcze zweryfikowane.", tone: "muted" },
                hasOrgAccount
                  ? { key: "org", icon: Building2, label: "Organizacja", tooltip: "Schronisko ma aktywne konto organizacji (właściciel).", tone: "primary" }
                  : { key: "org", icon: Building2, label: "Bez konta", tooltip: "Brak konta organizacji - schronisko nie ma jeszcze właściciela.", tone: "muted" },
                hasOrgAccount
                  ? { key: "link", icon: KeyRound, label: "Konto aktywne", tooltip: "Magic link został użyty - organizacja zalogowała się i ma konto.", tone: "primary" }
                  : hasActiveToken
                  ? { key: "link", icon: KeyRound, label: "Link aktywny", tooltip: "Magic link został wysłany i jest nadal aktywny (nieużyty, nie wygasł).", tone: "amber" }
                  : hasAnyToken
                  ? { key: "link", icon: KeyRound, label: "Link wygasł", tooltip: "Magic link był wysłany, ale wygasł lub został unieważniony. Wygeneruj nowy.", tone: "neutral" }
                  : { key: "link", icon: KeyRound, label: "Brak linku", tooltip: "Magic link nigdy nie został wygenerowany dla tego schroniska.", tone: "muted" },
              ];
              const toneClass = (tone: StatusPill["tone"]) =>
                tone === "primary"
                  ? "text-primary border-primary/40 bg-primary/5"
                  : tone === "amber"
                  ? "text-amber-700 border-amber-500/40 bg-amber-50 dark:text-amber-400 dark:bg-amber-950/30"
                  : tone === "neutral"
                  ? "text-muted-foreground border-border bg-muted/40"
                  : "text-muted-foreground/70 border-border/60 bg-transparent";

              return (
              <div
                key={shelter.id}
                className={`rounded-lg border bg-card hover:bg-muted/50 transition-colors ${
                  followUp
                    ? "border-amber-500/50 bg-amber-50/30 dark:bg-amber-950/10"
                    : "border-border"
                } ${verified ? "border-l-4 border-l-primary" : ""}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="relative flex-shrink-0 cursor-help mt-1">
                          <Building className="w-4 h-4 text-muted-foreground" />
                          <span
                            className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-card ${dataStatusColor}`}
                            aria-label={dataStatusLabel}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        {dataStatus === "complete" ? (
                          <p className="text-xs">Profil kompletny - wszystkie pola wypełnione</p>
                        ) : dataStatus === "empty" ? (
                          <p className="text-xs">Pusty profil - brak wszystkich danych</p>
                        ) : (
                          <div className="text-xs">
                            <p className="font-medium mb-1">Brakujące dane:</p>
                            <ul className="list-disc list-inside space-y-0.5">
                              {missingParts.map((m) => (
                                <li key={m as string}>{m}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setEditingShelter(shelter)}>
                    {/* Status pills row above name */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                      <TooltipProvider delayDuration={150}>
                        {statusPills.map((p) => {
                          const Icon = p.icon;
                          return (
                            <Tooltip key={p.key}>
                              <TooltipTrigger asChild>
                                <span
                                  className={`inline-flex items-center gap-1 text-[10px] leading-none px-1.5 py-1 rounded-full border cursor-help ${toneClass(p.tone)}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Icon className="w-3 h-3" />
                                  <span className="hidden sm:inline">{p.label}</span>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                <p className="text-xs">{p.tooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </TooltipProvider>
                      {shelter.organization_verification_pending && !verified && (
                        <Badge variant="outline" className="text-[10px] leading-none px-1.5 py-1 rounded-full text-amber-700 border-amber-500/40 bg-amber-50 dark:text-amber-400 dark:bg-amber-950/30">
                          Oczekuje
                        </Badge>
                      )}
                      {followUp && (
                        <Badge variant="outline" className="text-[10px] leading-none px-1.5 py-1 rounded-full text-amber-700 border-amber-500/50 bg-amber-50 dark:text-amber-400 dark:bg-amber-950/30 flex items-center gap-1">
                          <Flag className="w-3 h-3" />
                          Wymaga działania
                        </Badge>
                      )}
                    </div>
                    <p className="font-medium text-sm truncate hover:text-primary transition-colors">
                      {shelter.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {shelter.city}{shelter.region && `, ${shelter.region}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-wrap sm:flex-nowrap sm:flex-shrink-0 sm:ml-2">
                  <Button
                    size="sm"
                    variant={followUp ? "default" : "ghost"}
                    onClick={() => handleToggleFollowUp(shelter.id, followUp)}
                    disabled={toggleFollowUp.isPending}
                    title={followUp ? "Usuń oznaczenie 'Wymaga działania'" : "Oznacz jako 'Wymaga działania'"}
                    className={followUp ? "bg-amber-500 hover:bg-amber-600 text-white" : "text-muted-foreground"}
                  >
                    <Flag className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant={hasNote ? "default" : "ghost"}
                    onClick={() => toggleNoteExpand(shelter.id)}
                    title={hasNote ? "Edytuj notatkę" : "Dodaj notatkę"}
                    className={hasNote ? "" : "text-muted-foreground"}
                  >
                    <StickyNote className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      const url = `https://gdzieschronisko.pl/schronisko/${shelter.slug ?? shelter.id}`;
                      try {
                        await navigator.clipboard.writeText(url);
                        toast.success("Skopiowano link do profilu");
                      } catch {
                        toast.error("Nie udało się skopiować linku");
                      }
                    }}
                    title="Kopiuj link do profilu schroniska"
                    className="text-muted-foreground"
                  >
                    <Link2 className="w-4 h-4" />
                  </Button>
                  <InlineGenerateClaimLinkButton
                    shelterId={shelter.id}
                    shelterName={shelter.name}
                    defaultEmail={shelter.email}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingShelter(shelter)}
                    title="Edytuj schronisko"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {shelter.organization_verified ? (
                    <>
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        {shelter.organization_verified_at && 
                          format(new Date(shelter.organization_verified_at), "d MMM yyyy", { locale: pl })
                        }
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnverify(shelter.id, shelter.name)}
                        disabled={verifyOrganization.isPending}
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        title="Cofnij weryfikację organizacji"
                      >
                        {verifyOrganization.isPending ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <BadgeX className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleVerify(shelter.id, shelter.name)}
                      disabled={verifyOrganization.isPending}
                      className="h-7 w-7 p-0"
                      title="Zweryfikuj organizację"
                    >
                      {verifyOrganization.isPending ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <BadgeCheck className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  )}

                  {/* Delete shelter button with confirmation */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        disabled={deleteShelter.isPending}
                      >
                        {deleteShelter.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Usunąć schronisko?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Czy na pewno chcesz usunąć <strong>"{shelter.name}"</strong> z bazy danych?
                          <br /><br />
                          Ta operacja jest nieodwracalna. Zostaną usunięte także wszystkie powiązane zgłoszenia i historia zmian.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Anuluj</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteShelter(shelter.id, shelter.name)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Usuń schronisko
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                </div>
                {isExpanded && (
                  <div className="px-3 pb-3">
                    <AdminNoteInline
                      shelterId={shelter.id}
                      initialNotes={noteText}
                      initialUpdatedAt={noteData?.updated_at ?? null}
                    />
                  </div>
                )}
              </div>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <Search className="w-10 h-10 mx-auto mb-3" />
              <p className="font-medium">Nie znaleziono schronisk</p>
              <p className="text-sm">Spróbuj zmienić kryteria wyszukiwania</p>
            </CardContent>
          </Card>
        )}
      </div>
      <ShelterEditDialog
        shelter={editingShelter}
        open={!!editingShelter}
        onOpenChange={(open) => { if (!open) setEditingShelter(null); }}
      />
    </div>
  );
};